/**
 * Production Audit Trail Service
 * 
 * Tracks all changes to production data for regulatory compliance.
 * Oil companies are legally required to maintain audit trails for royalty payments.
 * 
 * Schema: companies/{companyId}/audit_logs/{logId}
 * 
 * Features:
 * - Who entered/modified data
 * - What was changed (field-level diff)
 * - When it was changed
 * - Device/location info
 * - Immutable append-only logs
 */

import { 
  collection, 
  addDoc, 
  query, 
  where, 
  orderBy, 
  getDocs,
  Timestamp,
  limit,
  startAfter,
  DocumentSnapshot
} from 'firebase/firestore';
import { db } from '../firebase/client';
import { getCompanyId } from '../tenantContext';
import { authService } from '../auth/authService';

// ============================================================================
// TYPES
// ============================================================================

export type AuditAction = 
  | 'CREATE'      // New entry created
  | 'UPDATE'      // Entry modified
  | 'DELETE'      // Entry deleted (soft delete)
  | 'VERIFY'      // Manager verified entry
  | 'REJECT'      // Manager rejected entry
  | 'RESTORE'     // Restored from deleted
  | 'EXPORT'      // Data exported
  | 'VIEW';       // Sensitive data viewed

export type AuditEntityType = 
  | 'production_entry'
  | 'well'
  | 'tank_reading'
  | 'service_ticket'
  | 'haul_ticket'
  | 'invoice';

export interface FieldChange {
  field: string;
  oldValue: any;
  newValue: any;
  displayName?: string; // Human-readable field name
}

export interface AuditLogEntry {
  id?: string;
  
  // Who
  userId: string;
  userName: string;
  userEmail: string;
  userRole: string;
  
  // What
  action: AuditAction;
  entityType: AuditEntityType;
  entityId: string;
  entityName?: string; // e.g., "Alpha-5" for well name
  
  // When
  timestamp: Date;
  
  // Where (for mobile field workers)
  deviceInfo?: {
    platform: 'web' | 'ios' | 'android';
    browser?: string;
    appVersion?: string;
  };
  location?: {
    latitude: number;
    longitude: number;
    accuracy?: number;
  };
  ipAddress?: string;
  
  // Changes (for UPDATE actions)
  changes?: FieldChange[];
  previousValues?: Record<string, any>;
  newValues?: Record<string, any>;
  
  // Context
  wellId?: string;
  wellName?: string;
  entryDate?: string; // For production entries
  notes?: string;
  
  // Tenant
  companyId: string;
}

export interface AuditQueryOptions {
  entityType?: AuditEntityType;
  entityId?: string;
  wellId?: string;
  userId?: string;
  action?: AuditAction;
  startDate?: Date;
  endDate?: Date;
  limit?: number;
  startAfterDoc?: DocumentSnapshot;
}

export interface AuditSummary {
  totalEntries: number;
  entriesByUser: Record<string, { count: number; userName: string }>;
  entriesByAction: Record<AuditAction, number>;
  entriesByWell: Record<string, { count: number; wellName: string }>;
  dateRange: { start: Date; end: Date };
}

// ============================================================================
// FIELD DISPLAY NAMES
// ============================================================================

const FIELD_DISPLAY_NAMES: Record<string, string> = {
  gasVolumeMcf: 'Gas Volume (MCF)',
  oilVolumeBbl: 'Oil Volume (BBL)',
  waterVolumeBbl: 'Water Volume (BBL)',
  totalSalesBbl: 'Total Sales (BBL)',
  casingPressurePsi: 'Casing Pressure (PSI)',
  tubingPressurePsi: 'Tubing Pressure (PSI)',
  flowlinePressurePsi: 'Flowline Pressure (PSI)',
  maintenanceCost: 'Maintenance Cost ($)',
  notes: 'Notes',
  status: 'Status',
  tankGauges: 'Tank Readings',
  photos: 'Photos',
  kolorKut: 'Kolor Kut Reading'
};

// ============================================================================
// AUDIT SERVICE CLASS
// ============================================================================

class AuditTrailService {
  private readonly COLLECTION = 'audit_logs';

  /**
   * Log a production entry creation
   */
  async logCreate(
    entityType: AuditEntityType,
    entityId: string,
    data: Record<string, any>,
    context?: { wellId?: string; wellName?: string; entryDate?: string }
  ): Promise<string> {
    return this.createLog({
      action: 'CREATE',
      entityType,
      entityId,
      entityName: context?.wellName,
      newValues: data,
      wellId: context?.wellId,
      wellName: context?.wellName,
      entryDate: context?.entryDate
    });
  }

  /**
   * Log a production entry update with field-level diff
   */
  async logUpdate(
    entityType: AuditEntityType,
    entityId: string,
    previousData: Record<string, any>,
    newData: Record<string, any>,
    context?: { wellId?: string; wellName?: string; entryDate?: string }
  ): Promise<string> {
    const changes = this.calculateChanges(previousData, newData);
    
    // Don't log if nothing actually changed
    if (changes.length === 0) {
      return '';
    }

    return this.createLog({
      action: 'UPDATE',
      entityType,
      entityId,
      entityName: context?.wellName,
      changes,
      previousValues: previousData,
      newValues: newData,
      wellId: context?.wellId,
      wellName: context?.wellName,
      entryDate: context?.entryDate
    });
  }

  /**
   * Log manager verification
   */
  async logVerify(
    entityType: AuditEntityType,
    entityId: string,
    notes?: string,
    context?: { wellId?: string; wellName?: string; entryDate?: string }
  ): Promise<string> {
    return this.createLog({
      action: 'VERIFY',
      entityType,
      entityId,
      entityName: context?.wellName,
      notes,
      wellId: context?.wellId,
      wellName: context?.wellName,
      entryDate: context?.entryDate
    });
  }

  /**
   * Log data export for compliance
   */
  async logExport(
    entityType: AuditEntityType,
    description: string,
    filters?: Record<string, any>
  ): Promise<string> {
    return this.createLog({
      action: 'EXPORT',
      entityType,
      entityId: 'bulk_export',
      notes: description,
      newValues: filters
    });
  }

  /**
   * Create the actual audit log entry
   */
  private async createLog(params: {
    action: AuditAction;
    entityType: AuditEntityType;
    entityId: string;
    entityName?: string;
    changes?: FieldChange[];
    previousValues?: Record<string, any>;
    newValues?: Record<string, any>;
    wellId?: string;
    wellName?: string;
    entryDate?: string;
    notes?: string;
  }): Promise<string> {
    const companyId = getCompanyId();
    if (!companyId) throw new Error('Tenant ID required for audit logging');

    const user = authService.getCurrentUser();
    if (!user) throw new Error('User must be authenticated for audit logging');

    const deviceInfo = this.getDeviceInfo();
    
    const logEntry: Omit<AuditLogEntry, 'id'> = {
      // Who
      userId: user.id,
      userName: user.name || user.email || 'Unknown User',
      userEmail: user.email || '',
      userRole: user.role || 'unknown',
      
      // What
      action: params.action,
      entityType: params.entityType,
      entityId: params.entityId,
      entityName: params.entityName,
      
      // When
      timestamp: new Date(),
      
      // Where
      deviceInfo,
      
      // Changes
      changes: params.changes,
      previousValues: params.previousValues,
      newValues: params.newValues,
      
      // Context
      wellId: params.wellId,
      wellName: params.wellName,
      entryDate: params.entryDate,
      notes: params.notes,
      
      // Company
      companyId
    };

    const collectionRef = collection(db, `companies/${companyId}/${this.COLLECTION}`);
    const docRef = await addDoc(collectionRef, {
      ...logEntry,
      timestamp: Timestamp.fromDate(logEntry.timestamp)
    });

    return docRef.id;
  }

  /**
   * Calculate field-level changes between two objects
   */
  private calculateChanges(oldData: Record<string, any>, newData: Record<string, any>): FieldChange[] {
    const changes: FieldChange[] = [];
    const allKeys = new Set([...Object.keys(oldData || {}), ...Object.keys(newData || {})]);

    // Skip metadata fields
    const skipFields = ['id', 'companyId', 'companyId', 'updatedAt', 'createdAt', 'timestamp'];

    for (const key of allKeys) {
      if (skipFields.includes(key)) continue;
      
      const oldValue = oldData?.[key];
      const newValue = newData?.[key];

      // Deep compare for objects/arrays
      if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
        changes.push({
          field: key,
          oldValue,
          newValue,
          displayName: FIELD_DISPLAY_NAMES[key] || this.formatFieldName(key)
        });
      }
    }

    return changes;
  }

  /**
   * Format camelCase field name to readable format
   */
  private formatFieldName(field: string): string {
    return field
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }

  /**
   * Get device/browser info
   */
  private getDeviceInfo(): AuditLogEntry['deviceInfo'] {
    if (typeof window === 'undefined') return undefined;

    const ua = navigator.userAgent;
    let platform: 'web' | 'ios' | 'android' = 'web';
    
    if (/android/i.test(ua)) platform = 'android';
    else if (/iPad|iPhone|iPod/.test(ua)) platform = 'ios';

    return {
      platform,
      browser: ua.substring(0, 100), // Truncate for storage
      appVersion: (window as any).__APP_VERSION__ || '1.0.0'
    };
  }

  // ============================================================================
  // QUERY METHODS
  // ============================================================================

  /**
   * Get audit logs for a specific well
   */
  async getLogsForWell(wellId: string, options?: { limit?: number; startDate?: Date; endDate?: Date }): Promise<AuditLogEntry[]> {
    return this.queryLogs({
      wellId,
      limit: options?.limit || 100,
      startDate: options?.startDate,
      endDate: options?.endDate
    });
  }

  /**
   * Get audit logs for a specific entry date (all wells)
   */
  async getLogsForDate(entryDate: string, options?: { wellId?: string; limit?: number }): Promise<AuditLogEntry[]> {
    const companyId = getCompanyId();
    if (!companyId) return [];

    const collectionRef = collection(db, `companies/${companyId}/${this.COLLECTION}`);
    
    let q = query(
      collectionRef,
      where('entryDate', '==', entryDate),
      orderBy('timestamp', 'desc'),
      limit(options?.limit || 100)
    );

    if (options?.wellId) {
      q = query(
        collectionRef,
        where('entryDate', '==', entryDate),
        where('wellId', '==', options.wellId),
        orderBy('timestamp', 'desc'),
        limit(options?.limit || 100)
      );
    }

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toDate() || new Date()
    })) as AuditLogEntry[];
  }

  /**
   * Get audit logs for a specific user
   */
  async getLogsForUser(userId: string, options?: { limit?: number; startDate?: Date; endDate?: Date }): Promise<AuditLogEntry[]> {
    return this.queryLogs({
      userId,
      limit: options?.limit || 100,
      startDate: options?.startDate,
      endDate: options?.endDate
    });
  }

  /**
   * Generic query method
   */
  async queryLogs(options: AuditQueryOptions): Promise<AuditLogEntry[]> {
    const companyId = getCompanyId();
    if (!companyId) return [];

    const collectionRef = collection(db, `companies/${companyId}/${this.COLLECTION}`);
    
    // Build query constraints
    const constraints: any[] = [];
    
    if (options.entityType) {
      constraints.push(where('entityType', '==', options.entityType));
    }
    if (options.entityId) {
      constraints.push(where('entityId', '==', options.entityId));
    }
    if (options.wellId) {
      constraints.push(where('wellId', '==', options.wellId));
    }
    if (options.userId) {
      constraints.push(where('userId', '==', options.userId));
    }
    if (options.action) {
      constraints.push(where('action', '==', options.action));
    }
    if (options.startDate) {
      constraints.push(where('timestamp', '>=', Timestamp.fromDate(options.startDate)));
    }
    if (options.endDate) {
      constraints.push(where('timestamp', '<=', Timestamp.fromDate(options.endDate)));
    }

    constraints.push(orderBy('timestamp', 'desc'));
    constraints.push(limit(options.limit || 100));

    if (options.startAfterDoc) {
      constraints.push(startAfter(options.startAfterDoc));
    }

    const q = query(collectionRef, ...constraints);
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toDate() || new Date()
    })) as AuditLogEntry[];
  }

  /**
   * Get summary statistics for audit logs
   */
  async getAuditSummary(startDate: Date, endDate: Date, wellId?: string): Promise<AuditSummary> {
    const logs = await this.queryLogs({
      startDate,
      endDate,
      wellId,
      limit: 1000 // Get more for summary
    });

    const entriesByUser: Record<string, { count: number; userName: string }> = {};
    const entriesByAction: Record<AuditAction, number> = {} as Record<AuditAction, number>;
    const entriesByWell: Record<string, { count: number; wellName: string }> = {};

    for (const log of logs) {
      // By user
      if (!entriesByUser[log.userId]) {
        entriesByUser[log.userId] = { count: 0, userName: log.userName };
      }
      entriesByUser[log.userId].count++;

      // By action
      entriesByAction[log.action] = (entriesByAction[log.action] || 0) + 1;

      // By well
      if (log.wellId) {
        if (!entriesByWell[log.wellId]) {
          entriesByWell[log.wellId] = { count: 0, wellName: log.wellName || log.wellId };
        }
        entriesByWell[log.wellId].count++;
      }
    }

    return {
      totalEntries: logs.length,
      entriesByUser,
      entriesByAction,
      entriesByWell,
      dateRange: { start: startDate, end: endDate }
    };
  }
}

export const auditTrailService = new AuditTrailService();
